import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { SelectString } from '@components/SelectString'
import type { Projectbetrokkene } from '@models/Projectbetrokkene'

import type { Discipline } from '../../../core/util/discipline'
import { PropertyRow } from '../../../theme/components/PropertyRow'
import { StyledError } from '../../../theme/components/StyledError'
import { useCreateProjectbetrokkene } from '../../hooks/useCreateProjectbetrokkene'

import { SelectDisciplines } from './SelectDisciplines'

export { CreateBetrokkene }

type Props = {
  /** de betrokkenen van één verantwoordelijke in één gebied */
  projectbetrokkenen: Projectbetrokkene[]
  projectType: Projectbetrokkene['projectType']
  onClose: () => void
}

const alleTypes: Projectbetrokkene['type'][] = [
  'Aannemer',
  'Combi',
  'Netbeheerder',
  'GedelegeerdNetbeheerder',
]

/**
 * Voor een verantwoordelijke netbeheerder kunnen geen andere netbeheerders
 * worden opgevoerd, ook geen gedelegeerde.
 */
const betrokkeneTypes = (
  verantwoordelijkeType?: Projectbetrokkene['type'],
): Projectbetrokkene['type'][] =>
  verantwoordelijkeType === 'Netbeheerder' ? ['Aannemer', 'Combi'] : alleTypes

const CreateBetrokkene = ({
  projectbetrokkenen,
  projectType,
  onClose,
}: Props) => {
  const { mutate: createProjectbetrokkene, error } =
    useCreateProjectbetrokkene()

  const [organisatie, setOrganisatie] = useState('')
  const [type, setType] = useState<Projectbetrokkene['type']>('Aannemer')
  const [delegerende, setDelegerende] = useState('')
  const [disciplines, setDisciplines] = useState<Discipline[]>([])
  const [errorText, setErrorText] = useState('')
  const [disciplinesErrorText, setDisciplinesErrorText] = useState('')

  const { verantwoordelijke, gebiedscode, gebiedscodeLabel } =
    projectbetrokkenen[0]

  // de 'eigen rij' van de verantwoordelijke bepaalt welke types mogen
  const types = betrokkeneTypes(
    projectbetrokkenen.find((x) => x.organisatie === x.verantwoordelijke)?.type,
  )

  const handleSubmit = () => {
    if (!organisatie) {
      setErrorText('Organisatie is verplicht')

      return
    }

    if (type === 'Netbeheerder' && disciplines.length === 0) {
      setDisciplinesErrorText('Minimaal één discipline is verplicht')

      return
    }

    createProjectbetrokkene(
      {
        projectbetrokkene: {
          projectType,
          verantwoordelijke,
          gebiedscode,
          gebiedscodeLabel: '',
          organisatie,
          type,
          ...(type === 'GedelegeerdNetbeheerder' ? { delegerende } : {}),
          ...(type === 'Netbeheerder' ? { disciplines } : {}),
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Betrokkene toevoegen</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PropertyRow label="Verantwoordelijke" value={verantwoordelijke} />

        <PropertyRow label="Gebied" value={gebiedscodeLabel} />

        <TextField
          label="Organisatie"
          value={organisatie}
          onChange={(e) => {
            setOrganisatie(e.target.value)
            setErrorText('')
          }}
          variant="standard"
          error={!!errorText}
          helperText={errorText}
        />

        <SelectString
          label="Type"
          options={types}
          value={type}
          onChange={(type) => setType(type)}
          disableClearable
        />

        {type === 'Netbeheerder' && (
          <SelectDisciplines
            projectType={projectType}
            value={disciplines}
            onChange={(value) => {
              setDisciplines(value)
              setDisciplinesErrorText('')
            }}
            errorText={disciplinesErrorText}
          />
        )}

        {type === 'GedelegeerdNetbeheerder' && (
          <TextField
            label="Delegerende"
            value={delegerende}
            onChange={(e) => setDelegerende(e.target.value)}
            variant="standard"
          />
        )}

        <StyledError error={error} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} autoFocus>
          Sluiten
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          endIcon={<AddIcon />}
        >
          Toevoegen
        </Button>
      </DialogActions>
    </Dialog>
  )
}
